import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getHSFSPurchaseUrl, getLatamEsPromotionUrl } from 'pricing-pages-lib/utils/urls';
import { allEligibleDiscountsSelector, promotionForSkuIdSelector } from 'pricing-pages-ui/selectors/PromotionSelectors';
import { useSelector } from 'react-redux';
import { CMS_HUB_PROFESSIONAL, OPERATIONS_HUB_PROFESSIONAL, SALES_HUB_PROFESSIONAL, SERVICE_HUB_PROFESSIONAL } from 'self-service-api/constants/ApiNames';
import { CMS, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { promotionKeys } from 'self-service-api/constants/Promotions';
import { DIST026_GROUP_PARAMETER_OPTIONS } from '../../constants/experimentKeys';
import { useDist026Group } from '../../hooks/dist026Hooks';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { currentSkuIdForProductSelector } from '../../selectors/ProductsSelectors';
import PromotionBanner from './PromotionBanner';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PromotionBannerContainer = ({
  productLine
}) => {
  const isAppReady = useGlobalLoading();
  const eligibleDiscounts = useSelector(allEligibleDiscountsSelector);
  const {
    dist026Group,
    dist026Active: experimentActive,
    dist026TreatmentFetched: treatmentFetched
  } = useDist026Group();
  const showHsfxCta = treatmentFetched && (dist026Group === DIST026_GROUP_PARAMETER_OPTIONS.control || !experimentActive);
  const hsfxCta = showHsfxCta ? {
    url: getHSFSPurchaseUrl(),
    message: 'pricingPage.promotions.hsfx.cta'
  } : undefined;
  const getProductPromotionEligibilityMap = useSelector(state => {
    return apiName => promotionForSkuIdSelector(state, {
      skuId: currentSkuIdForProductSelector(state, {
        apiName
      })
    });
  });

  // Returns a getter function that retrieves the copy keys for LATAM-ES
  const getRegionalHubPromotionCopyKeyGetter = apiName => {
    const copyKeyMap = {
      [SALES_HUB_PROFESSIONAL]: 'salesOnlyProfessional',
      [SERVICE_HUB_PROFESSIONAL]: 'serviceOnlyProfessional',
      [CMS_HUB_PROFESSIONAL]: 'cmsHub',
      [OPERATIONS_HUB_PROFESSIONAL]: 'operationsOnlyProfessional'
    };
    const copyKey = copyKeyMap[apiName];
    return promotionKey => {
      const professionalPromotionEligibilityMap = getProductPromotionEligibilityMap(apiName);
      if (professionalPromotionEligibilityMap && professionalPromotionEligibilityMap.eligiblePromotion === promotionKey) {
        return copyKey;
      }
      return null;
    };
  };
  const regionalPromotionCopyKeyGetter = {
    [SALES]: getRegionalHubPromotionCopyKeyGetter(SALES_HUB_PROFESSIONAL),
    [SERVICE]: getRegionalHubPromotionCopyKeyGetter(SERVICE_HUB_PROFESSIONAL),
    [CMS]: getRegionalHubPromotionCopyKeyGetter(CMS_HUB_PROFESSIONAL),
    [OPERATIONS]: getRegionalHubPromotionCopyKeyGetter(OPERATIONS_HUB_PROFESSIONAL)
  }[productLine];
  const isEligibleForPromotion = promotionKey => eligibleDiscounts.includes(promotionKey);
  if (!isAppReady) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [regionalPromotionCopyKeyGetter && /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: Boolean(regionalPromotionCopyKeyGetter(promotionKeys.LATAM_AND_BRAZIL_ES_PRO)),
      promotion: promotionKeys.LATAM_AND_BRAZIL_ES_PRO,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.latamEs.${regionalPromotionCopyKeyGetter(promotionKeys.LATAM_AND_BRAZIL_ES_PRO)}.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.latamEs.${regionalPromotionCopyKeyGetter(promotionKeys.LATAM_AND_BRAZIL_ES_PRO)}.body_jsx`,
        elements: {
          UILink: Link
        },
        options: {
          url: getLatamEsPromotionUrl()
        }
      }),
      dataSelenium: `${promotionKeys.LATAM_AND_BRAZIL_ES_PRO.toLowerCase()}-promotion-header`
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFS_SEED_V2),
      promotion: promotionKeys.HSFS_SEED_V2,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.seed.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.seed.body_jsx`,
        elements: {}
      }),
      cta: hsfxCta
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFX_ENTREPRENEUR_V2),
      promotion: promotionKeys.HSFX_ENTREPRENEUR_V2,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.entrepreneur.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.entrepreneur.body_jsx`,
        elements: {}
      }),
      cta: hsfxCta
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFX_BOOTSTRAP),
      promotion: promotionKeys.HSFX_BOOTSTRAP,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.bootstrap.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.bootstrap.body_jsx`,
        elements: {}
      }),
      cta: hsfxCta
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.STRATEGIC_PARTNERSHIP),
      promotion: promotionKeys.STRATEGIC_PARTNERSHIP,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.promotions.hsfx.strategicPartner.title"
      }),
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.promotions.hsfx.strategicPartner.body"
      }),
      cta: hsfxCta
    }), /*#__PURE__*/_jsx(PromotionBanner, {
      shouldShow: isEligibleForPromotion(promotionKeys.HSFS_SERIES_A_V2),
      promotion: promotionKeys.HSFS_SERIES_A_V2,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `pricingPage.promotions.hsfx.series-a.title`
      }),
      description: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `pricingPage.promotions.hsfx.series-a.body_jsx`,
        elements: {}
      }),
      cta: hsfxCta
    })]
  });
};
export default PromotionBannerContainer;